import DOMPurify from 'dompurify';
import React, { useEffect, useRef, useState } from 'react';
import { getTooltipProps } from '../typography/typography-utils';
/**
 * SafeHTML is a thin wrapper over React's dangerouslySetInnerHTML.
 * It sanitizes the input HTML string using dompurify's sanitize function.
 *
 * Example:
 *
 * - Use this:
 *
 * ```
 * <SafeHTML html={myHTMLString} />
 * ```
 *
 * - Don't use this:
 *
 * ```
 * <div dangerouslySetInnerHTML={{ __html: myHTMLString }} />
 * ```
 */
export const SafeHTML = ({ html, className, config, tooltipConfig, ...restProps }) => {
    const ref = useRef(null);
    const [isEllipsisShowing, setIsEllipsisShowing] = useState(false);
    const { tooltipProps, sanitizedHTMLString } = React.useMemo(() => {
        let sanitizedHTML;
        if (config) {
            DOMPurify.addHook('afterSanitizeAttributes', (node) => {
                if ((node === null || node === void 0 ? void 0 : node.nodeName) === 'A' &&
                    node.getAttribute('target') === '_blank') {
                    node.setAttribute('rel', 'noopener noreferrer');
                }
            });
            sanitizedHTML = DOMPurify.sanitize(html, {
                ...config,
                RETURN_DOM_FRAGMENT: false,
                RETURN_DOM: false,
            });
            DOMPurify.removeAllHooks();
        }
        else {
            sanitizedHTML = DOMPurify.sanitize(html);
        }
        return {
            tooltipProps: getTooltipProps(sanitizedHTML || html, tooltipConfig, isEllipsisShowing),
            sanitizedHTMLString: sanitizedHTML,
        };
    }, [isEllipsisShowing, config, tooltipConfig, html]);
    useEffect(() => {
        if (ref.current &&
            (ref.current.clientHeight < ref.current.scrollHeight ||
                ref.current.clientWidth < ref.current.scrollWidth)) {
            setIsEllipsisShowing(true);
        }
        else {
            setIsEllipsisShowing(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref === null || ref === void 0 ? void 0 : ref.current]);
    // When DOMPurify.sanitize(html) fails due to invalid html, it return empty string.
    // So, to handle that behaviour we need to return the value as a string itself
    return sanitizedHTMLString ? (React.createElement("div", { className: className, ref: ref, 
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML: { __html: sanitizedHTMLString }, ...tooltipProps, ...restProps })) : (React.createElement("div", { className: className, ref: ref, ...tooltipProps, ...restProps }, html));
};
/**
 *
 * @param text text to check
 * @returns it returns boolean  on the basis of given test has HTML Tags present or not
 */
export const isHTML = (text) => {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
};
//# sourceMappingURL=safe-html.js.map